<template>
  <file-store-uploader
    :folder-id="folderId"
    @complete="onUploadCompleted"
    @progress="onUploadProgress"
    class="file-browser-container"
    :enabled="!dragging && folder && folder.permissions !== 'read'"
  >
    <header>
      <table>
        <colgroup>
          <col style="width: 37px;" />
          <col />
          <col style="width: 75px;" />
          <col style="width: 89px;" />
          <col
            :style="[files.length > 9 ? { width: '44px' } : { width: '38px' }]"
          />
        </colgroup>
        <thead>
          <tr>
            <th>
              <select-box
                v-if="fileCount > 0"
                @action="selectAll"
                :selected="fileCount === selectedFileIds.length"
              ></select-box>
            </th>
            <th
              style="text-align: left;"
              class="clickable"
              @click="onSort('name')"
            >
              Naam
            </th>
            <th class="align-right clickable" @click="onSort('fileSize')">
              Grootte
            </th>
            <th class="align-right clickable" @click="onSort('created')">
              Toegevoegd
            </th>
            <th></th>
          </tr>
        </thead>
      </table>
    </header>
    <main class="file-browser-main">
      <table>
        <colgroup>
          <col style="width: 37px;" />
          <col style="width: 100%;" />
          <col style="width: 75px;" />
          <col style="width: 89px;" />
          <col style="width: 38px;" />
        </colgroup>
        <tbody>
          <tr v-if="files.length < 1">
            <td colspan="5">
              <span v-if="folder && !folder.parentId"
                >Dit archief is nog leeg</span
              >
              <span v-else>Deze map is nog leeg</span>
            </td>
          </tr>
          <tr v-for="(file, index) in files" :key="index" :class="[file.type]">
            <td>
              <select-box
                v-if="file.type === 'file'"
                @action="toggleSelectedFile(file.fileId)"
                :selected="selectedFileIds.includes(file.fileId)"
              ></select-box>

              <template v-if="file.type === 'folder'">
                <img
                  src="../../../images/folder-shadow.svg"
                  width="24"
                  height="24"
                />
              </template>
            </td>
            <td @click="fileOrFolderClicked(file)" class="filename">
              <span>{{ file.name }}</span>
              <i
                v-if="
                  file.type === 'file' && fileIdsInGlobe.includes(file.fileId)
                "
                class="select-badge"
              ></i>
            </td>
            <td class="align-right" @click="fileOrFolderClicked(file)">
              {{ $filters.formatFileSize(file.fileSize) }}
            </td>
            <td class="align-right" @click="fileOrFolderClicked(file)">
              {{ $filters.formatDate(file.created) }}
            </td>
            <td>
              <popup-menu
                anchor-position="right"
                v-if="showKebabForFile(file)"
                :title="file.name"
              >
                <template #trigger>
                  <button class="kebab-btn"><span></span></button>
                </template>
                <template
                  v-if="file.type === 'file' && selectedFileIds.length > 1"
                >
                  <menu-item @click="() => addSelectedToFileList()">
                    Aan winkelwagen toevoegen
                  </menu-item>
                  <menu-item @click="() => download()">Downloaden</menu-item>
                  <template v-if="folder && folder.permissions !== 'read'">
                    <menu-item @click="() => trashSelectedFiles()"
                      >Naar prullenmand
                    </menu-item>
                  </template>
                </template>

                <template v-else-if="file.type === 'file'">
                  <menu-item @click="() => addToFileList(file)">
                    Aan winkelwagen toevoegen
                  </menu-item>
                  <menu-item
                    v-if="viewableFileType(file)"
                    @click="() => viewFile(file)"
                    >Bekijken
                  </menu-item>
                  <menu-item @click="() => download(file)"
                    >Downloaden
                  </menu-item>
                  <!--   <menu-item @click="() => shareFile(file)">Delen</menu-item> -->
                  <menu-item @click="() => getFileInfo(file)">Info</menu-item>
                  <template v-if="folder && folder.permissions !== 'read'">
                    <menu-item @click="() => trashFile(file)"
                      >Naar prullenmand
                    </menu-item>
                  </template>
                </template>

                <template v-else-if="file.type === 'folder'">
                  <menu-item @click="() => getFileInfo(file)">Info</menu-item>
                  <template v-if="folder && folder.permissions !== 'read'">
                    <menu-item @click="() => trashFolder(file)"
                      >Naar prullenmand
                    </menu-item>
                  </template>
                </template>
              </popup-menu>
            </td>
          </tr>
          <!-- dummy row so all content is visible when scrolling down -->
          <tr style="height: 70px;"></tr>
        </tbody>
      </table>
    </main>
    <footer>
      <template v-if="folder && folder.permissions !== 'read'">
        <uploader-btn>
          <div class="pill-button upload-button" :class="{ glow: uploading }">
            <div v-if="!uploading">BESTAND UPLOADEN</div>
            <div v-else>{{ uploadPercentage }}%</div>
          </div>
        </uploader-btn>
        <button type="button" class="pill-button" @click="addFolder()">
          NIEUWE MAP
        </button>
      </template>
    </footer>
  </file-store-uploader>
</template>

<script>
import FileNavigationMixin from '@/features/filestore/FileNavigationMixin.js'
import NameDialog from '@/features/filestore/dialogs/NameDialog.vue'
import * as fileStoreService from '@/features/filestore/FileStoreService.js'
import FileStoreUploader from '@/components/ui/file-upload/FileStoreUploader.vue'

import SortMixin from '@/mixins/SortMixin'
import { EventBus, Events } from '@/eventbus/EventBus'
import FileTransferModal from '@/dialogs/FileTransfer.vue'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import { mapStores } from 'pinia'

import { useFileListStore } from '@/stores/FileList.store'
import { useFileStoreStore } from '@/features/filestore/FileStore.store'
import { he } from 'date-fns/locale'

export default {
  props: ['folderId', 'archiveId'],
  mixins: [FileNavigationMixin, SortMixin],
  components: { FileStoreUploader },
  data() {
    return {
      hoverIndex: undefined,
      uploading: false,
      uploadPercentage: 0,
      sortField: 'name',
      sortReverse: false
    }
  },
  created() {
    this.loadFiles(this.folderId)

    EventBus.on(Events.FOLDER_NOT_FOUND, () => {
      this.$modal.show(
        ErrorMessage,
        {
          title: 'Map niet gevonden',
          text: 'De map die u zoekt bestaat niet meer.'
        },
        {
          width: 380,
          adaptive: true
        }
      )

      this.$router.push({ name: 'fileStoreDashboard' })
    })
  },
  watch: {
    folderId(value) {
      this.loadFiles(value)
      this.fileStoreStore.clearBrowserSelectedFileIds()
    }
  },
  unmounted() {
    this.fileStoreStore.clearBrowserSelectedFileIds()
  },
  computed: {
    ...mapStores(useFileListStore, useFileStoreStore),
    folder() {
      return this.fileStoreStore.getFolder(this.folderId)
    },
    files() {
      const folders = this.fileStoreStore.getSubFolders(this.folderId) || []
      const files = this.fileStoreStore.getFilesForFolder(this.folderId) || []

      return [
        ...this.sortArray(folders, this.sortField, this.sortReverse),
        ...this.sortArray(files, this.sortField, this.sortReverse)
      ]
    },
    fileCount() {
      return this.fileStoreStore.getFilesForFolder(this.folderId).length
    },
    selectedFileIds() {
      return this.fileStoreStore.browserSelectedFileIds
    },
    dragging() {
      return this.fileStoreStore.dragging
    },
    fileIdsInGlobe() {
      return this.fileListStore.selectedFileIds || []
    }
  },
  methods: {
    loadFiles(folderId) {
      this.fileStoreStore.loadFilesForFolder(folderId)
    },
    addFolder() {
      this.$modal.show(
        NameDialog,
        {
          name: '',
          title: 'Nieuwe map',
          actionLabel: 'Voeg toe',
          callback: (name) => {
            fileStoreService.addFolder(this.folderId, name)
          }
        },
        { width: 300, height: 'auto' }
      )
    },
    onUploadProgress(value) {
      this.uploading = true
      this.uploadPercentage = value
    },
    onUploadCompleted(params) {
      setTimeout(() => {
        if (params.foldersUploaded) {
          this.fileStoreStore.refreshFolders()
        }
        this.loadFiles(this.folderId)
        this.uploading = false
        this.uploadPercentage = 0
      }, 500)
    },
    selectAll() {
      if (this.fileCount === this.selectedFileIds.length) {
        this.files
          .filter((file) => file.type === 'file')
          .forEach((file) =>
            this.fileStoreStore.browserDeSelectFileId(file.fileId)
          )
      } else {
        this.files
          .filter((file) => file.type === 'file')
          .forEach((file) =>
            this.fileStoreStore.browserSelectedFileId(file.fileId)
          )
      }
    },
    viewableFileType(file) {
      return ['image/jpeg', 'image/png', 'application/pdf'].includes(
        file.mimeType
      )
    },
    showKebabForFile(file) {
      // Display kebab:
      // - when nothing selected
      // - when file is selected
      return (
        this.selectedFileIds.length === 0 ||
        this.selectedFileIds.indexOf(file.fileId) > -1
      )
    },
    addToFileList(file) {
      EventBus.emit(Events.ADD_FILE_STORE_FILES_TO_SELECTION, [file.fileId])
    },
    addSelectedToFileList() {
      EventBus.emit(
        Events.ADD_FILE_STORE_FILES_TO_SELECTION,
        this.selectedFileIds
      )
    },
    download(file) {
      // Bij een meervoudige selectie wordt download() zonder file aangeroepen
      const toDownload =
        this.selectedFileIds.length > 1
          ? this.selectedFileIds
          : file
            ? [file.fileId]
            : this.selectedFileIds

      this.downloadFile(toDownload)
    }
  }
}
</script>

<style lang="scss" scoped>
.file-browser-container {
  height: 100%;
  display: grid;
  grid-template-rows: 39px 1fr;

  .file-browser-main {
    overflow: auto;
  }

  table {
    table-layout: fixed;
    width: 100%;
    border-collapse: collapse;
    color: var(--text-color);

    thead {
      border-bottom: solid 1px #e6e6e6;

      tr {
        height: 38px;

        th:first-child,
        th:last-child {
          padding-left: 10px;
        }

        th {
          vertical-align: middle;
          padding-left: 5px;
          padding-right: 5px;

          .fs-align-left {
            text-align: left;
          }
        }
      }
    }

    tbody {
      //padding-bottom: 70px;
      tr {
        height: 48px;

        transition: background-color 0.6s;

        cursor: pointer;

        td:first-child,
        td:last-child {
          padding-left: 10px;
        }

        &.folder {
          font-weight: 600;
        }

        &:hover {
          background-color: var(--hover-color);
        }

        td {
          vertical-align: middle;
          padding-left: 5px;
          padding-right: 5px;
        }
      }
    }
  }

  .filename {
    overflow: hidden;
    text-overflow: ellipsis;

    span {
      white-space: nowrap;
    }
  }

  footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      #ffffff 100%
    );

    button:last-child {
      margin-left: 10px;
    }

    // Dirty trick to overrule default uploader-btn styling
    .uploader-btn {
      display: inline-block;
      position: relative;
      padding: 0;
      font-size: unset;
      line-height: 0;
      color: #666;
      border: none;
      background: none;
      outline: none;
    }
  }
}

.file-drag-image-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
}

.upload-button {
  width: 144px;
  display: flex;
  justify-content: center;
  transition: box-shadow 0.5s;

  &.glow {
    animation: pulse 1.5s infinite;
  }
}

@keyframes pulse {
  0% {
    box-shadow: var(--sub-color) 0px 0px 19px 0px;
  }
  50% {
    box-shadow: var(--sub-color) 0px 0px 8px 0px;
  }
  100% {
    box-shadow: var(--sub-color) 0px 0px 19px 0px;
  }
}

.select-badge {
  margin-left: 8px;
  display: inline-block;
  background-color: var(--sub-color);
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.empty-text {
  padding: 20px 0 0 42px;
  width: 190px;
}
</style>
